import { Button as CollectrButton } from "./components/ui/button";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Suspense, lazy, useEffect, useRef, type ComponentType, type LazyExoticComponent } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
  useParams,
} from "react-router-dom";

import { Sentry } from "@/lib/sentry";
import { AdminMfaGate } from "./components/AdminMfaGate";
import { AppHomeRedirect } from "./components/AppHomeRedirect";
import { PaymentIntegrationRoute } from "./components/PaymentIntegrationRoute";
import { OpenBankingIntegrationRoute } from "./components/OpenBankingIntegrationRoute";
import { PaymentReconciliationRoute } from "./components/PaymentReconciliationRoute";
import { RouteErrorPage } from "./components/RouteErrorPage";
import { TenantGovernanceRoute } from "./components/TenantGovernanceRoute";
import { useAuth } from "./hooks/useAuth";
import { decodeJwtPayload } from "./domain/auth/adminMfaPolicy";
import { useCurrentUserRole } from "./hooks/useCurrentUserRole";
import { isDeadSessionError } from "./lib/deadSession";
import { loadRouteModule } from "./lib/routeModule";
import { reportHandledError } from "./lib/observability";
import supabase from "./lib/supabase";
import { magNogHerladen, noteerChunkGeladen, noteerPreloadFout } from "./lib/staleDeployment";
import { BILLING_UI_ENABLED, SETTLEMENT_UI_ENABLED } from "./lib/billingFlags";
import { canAccessPlatformAdmin, isPlatformSupportRole } from "./lib/platformAccess";
import { fetchUserAppAccessAllowed } from "./services/authAccessService";
import { isCsvImportEnabled } from "./lib/import/importFeatureFlag";
import { isDossierbedieningPrototypeEnabled } from "./prototypes/dossierbediening/prototypeFlag";

/**
 * De CSV-importingang staat achter een vlag (CSV-5, P7).
 *
 * Buiten de vlag om is er geen route: een operator die het pad intypt krijgt
 * dezelfde 404 als bij elk ander onbestaand scherm, in plaats van een wizard
 * die pas bij de laatste stap blijkt te falen. Op moduleniveau afgeleid omdat
 * `import.meta.env` bij het bouwen wordt ingevuld en tijdens een sessie niet
 * verandert.
 */
const csvImportEnabled = isCsvImportEnabled(
  import.meta.env.VITE_CSV_IMPORT_ENABLED as string | undefined,
);

/**
 * WP-UI0.1 — het dossierbedieningsprototype op fixtures.
 *
 * TWEE VOORWAARDEN, CUMULATIEF. `import.meta.env.DEV` staat vóór de vlag, en dat
 * is geen stijlkeuze: Vite vervangt hem bij `vite build` door de letterlijke
 * `false`, waarna de bundler de hele constante, de route én de dynamische import
 * wegsnijdt. Het gevolg is dat een productieartefact het prototype niet alleen
 * niet TOONT maar het ook niet BEVAT — geen routepad, geen chunk, geen
 * fixtureteksten, ongeacht welke omgevingsvariabele iemand zet.
 *
 * Dat verschil was gemeten en niet theoretisch: met alleen de vlag stond het
 * routepad van het prototype in de productiebundel en werd een chunk van 34 KB
 * met de fixtures geëmitteerd. De route werd niet geregistreerd, maar
 * de code lag er wel. `check:prototype-not-in-build` bouwt nu met de vlag AAN en
 * bewijst dat er niets van overblijft.
 *
 * Gevolg voor de review: het prototype is uitsluitend lokaal te bekijken, via
 * `npm run dev` met de vlag aan. Dat is de bewust gekozen reviewvorm.
 */
const dossierbedieningPrototypeEnabled =
  import.meta.env.DEV &&
  isDossierbedieningPrototypeEnabled(
    import.meta.env.VITE_DOSSIERBEDIENING_PROTOTYPE_ENABLED as string | undefined,
  );

const DossierbedieningPrototypePage = dossierbedieningPrototypeEnabled
  ? lazyPagina(() => import("./prototypes/dossierbediening/DossierbedieningPrototypePage"))
  : null;
const queryClient = new QueryClient();

function LegalExecutorToDossierRedirect() {
  const { dossierId } = useParams<{ dossierId: string }>();
  if (!dossierId || dossierId.trim() === "") {
    return <Navigate to="/app/legal-cases" replace />;
  }
  return <Navigate to={`/app/dossiers/${dossierId}`} replace />;
}

/**
 * UI-1 — `/app/dossiers/:id/v2` is geen weergave meer maar een doorverwijzing.
 *
 * Het experimentpad heeft een half jaar in bladwijzers en gedeelde links
 * gezeten. Het zomaar laten vervallen zou die allemaal op de "pagina niet
 * gevonden" laten landen; het als tweede route laten bestaan zou de twee
 * werkelijkheden in stand houden die deze slice juist opheft. Het tabblad uit
 * de zoekstring reist mee, zodat een gedeelde deeplink op dezelfde plek uitkomt.
 */
function DossierV2NaarCanoniekPad() {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  if (!id || id.trim() === "") return <Navigate to="/app/dossiers" replace />;
  return <Navigate to={`/app/dossiers/${id}${search}`} replace />;
}

function OpdrachtgeverDetailRedirect() {
  const { id } = useParams<{ id: string }>();
  if (!id || id.trim() === "") {
    return <Navigate to="/app/opdrachtgevers" replace />;
  }
  return <Navigate to={`/app/opdrachtgevers/${id}`} replace />;
}

function RecoveryDesignerListRedirect() {
  return <Navigate to="/app/recovery-designer" replace />;
}

function RecoveryDesignerDetailRedirect() {
  const { programId } = useParams<{ programId: string }>();
  if (!programId || programId.trim() === "") {
    return <Navigate to="/app/recovery-designer" replace />;
  }
  return <Navigate to={`/app/recovery-designer/${programId}`} replace />;
}

/**
 * Elke lazy pagina meldt dat zij WERKELIJK geladen is. Dat is het enige signaal
 * dat de herlaadteller mag wissen — het `load`-event van het document zei niets
 * over de chunk en maakte de grendel daardoor onwerkzaam.
 */
function lazyPagina<T extends ComponentType<Record<string, unknown>>>(
  laad: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    const module = await loadRouteModule(laad);
    if (typeof window !== "undefined") noteerChunkGeladen(window.sessionStorage);
    return module;
  });
}

const AppLayout = lazyPagina(() => import("./layouts/AppLayout"));
const LoginPage = lazyPagina(() => import("./pages/LoginPage"));
const WachtwoordHerstellenPage = lazyPagina(() => import("./pages/WachtwoordHerstellenPage"));
const TweefactorPage = lazyPagina(() => import("./pages/TweefactorPage"));
const LegalExportPage = lazyPagina(() => import("./pages/public/LegalExportPage"));
const ExplorerPage = lazyPagina(() => import("./pages/app/ExplorerPage"));
const DossiersPage = lazyPagina(() => import("./pages/app/DossiersPage"));
const ArchiefPage = lazyPagina(() => import("./pages/app/ArchiefPage"));
const PdfIntakePage = lazyPagina(() => import("./pages/app/PdfIntakePage"));
const DossierIntakePage = lazyPagina(() => import("./pages/app/DossierIntakePage"));
const PreDossierIntakesPage = lazyPagina(() => import("./pages/app/PreDossierIntakesPage"));
const PreDossierIntakeDetailPage = lazyPagina(() => import("./pages/app/PreDossierIntakeDetailPage"));
const IntakeQueuePage = lazyPagina(() => import("./pages/app/IntakeQueuePage"));
const ImportWizardPage = lazyPagina(() => import("./pages/app/ImportWizardPage"));
const DossierActivatiePage = lazyPagina(() => import("./pages/app/DossierActivatiePage"));
const IntakeQueueDetailPage = lazyPagina(() => import("./pages/app/IntakeQueueDetailPage"));
const ExceptionsPage = lazyPagina(() => import("./pages/app/ExceptionsPage"));
const WorkflowMonitorPage = lazyPagina(() => import("./pages/app/workflow-monitor/WorkflowMonitorPage"));
const DashboardPage = lazyPagina(() => import("./pages/app/DashboardPage"));
const DossierDetailPage = lazyPagina(() => import("./pages/app/DossierDetailPage"));
const IntakeFlowControlPage = lazyPagina(() => import("./pages/app/IntakeFlowControlPage"));
const LegalHoldPage = lazyPagina(() => import("./pages/app/LegalHoldPage"));
const ActivatiecentrumPage = lazyPagina(() => import("./pages/app/ActivatiecentrumPage"));
const UitnodigingPage = lazyPagina(() => import("./pages/UitnodigingPage"));
const OnboardingPage = lazyPagina(() => import("./pages/app/OnboardingPage"));
const WorkqueuePage = lazyPagina(() => import("./pages/app/WorkqueuePage"));
const FollowUpPage = lazyPagina(() => import("./pages/app/FollowUpPage"));
const OpvolgingPage = lazyPagina(() => import("./pages/app/OpvolgingPage"));
const OverdrachtenPage = lazyPagina(() => import("./pages/app/OverdrachtenPage"));
const LegalCasesPage = lazyPagina(() => import("./pages/app/LegalCasesPage"));
const PrintQueuePage = lazyPagina(() => import("./pages/app/PrintQueuePage"));
const TemplatesPage = lazyPagina(() => import("./pages/app/TemplatesPage"));
const TemplateDetailPage = lazyPagina(() => import("./pages/app/TemplateDetailPage"));
const TemplateEditPage = lazyPagina(() => import("./pages/app/TemplateEditPage"));
const InternalPlatformAdminPage = lazyPagina(() => import("./pages/app/InternalPlatformAdminPage"));
const DebiteurenPage = lazyPagina(() => import("./pages/app/DebiteurenPage"));
const DebiteurDetailPage = lazyPagina(() => import("./pages/app/DebiteurDetailPage"));
const OpdrachtgeversListPage = lazyPagina(() => import("./pages/app/OpdrachtgeversListPage"));
const OpdrachtgeverWizardPage = lazyPagina(() => import("./pages/app/OpdrachtgeverWizardPage"));
const OpdrachtgeverDetailPage = lazyPagina(() => import("./pages/app/OpdrachtgeverDetailPage"));
const CreditorProjectNewPage = lazyPagina(() => import("./pages/app/CreditorProjectNewPage"));
const CreditorProjectDetailPage = lazyPagina(() => import("./pages/app/CreditorProjectDetailPage"));
const PlatformTenantsListPage = lazyPagina(() => import("./pages/app/platform/PlatformTenantsListPage"));
const PlatformLegalDocsPage = lazyPagina(() => import("./pages/app/platform/PlatformLegalDocsPage"));
const LegalRequestPage = lazyPagina(() => import("./pages/LegalRequestPage"));
const PlatformTenantDetailPage = lazyPagina(() => import("./pages/app/platform/PlatformTenantDetailPage"));
const PlatformProvisionAdminPage = lazyPagina(() => import("./pages/app/platform/PlatformProvisionAdminPage"));
const PlatformRecoveryLibraryPage = lazyPagina(() => import("./pages/app/platform/PlatformRecoveryLibraryPage"));
const PlatformServicesPage = lazyPagina(() => import("./pages/app/platform/PlatformServicesPage"));
const PlatformColetteConfigPage = lazyPagina(() => import("./pages/app/platform/PlatformColetteConfigPage"));
const InstellingenJuridischeDocumentenPage = lazyPagina(() => import("./pages/app/settings/InstellingenJuridischeDocumentenPage"));
const SettingsHomePage = lazyPagina(() => import("./pages/app/SettingsHomePage"));
const InstellingenKostenPage = lazyPagina(() => import("./pages/app/settings/InstellingenKostenPage"));
const InstellingenDossiernummeringPage = lazyPagina(() => import("./pages/app/settings/InstellingenDossiernummeringPage"));
const InstellingenStandaardTarievenPage = lazyPagina(
  () => import("./pages/app/settings/InstellingenStandaardTarievenPage"),
);
const InstellingenKostenInterestPage = lazyPagina(() => import("./pages/app/settings/InstellingenKostenInterestPage"));
const InstellingenDemodataPage = lazyPagina(() => import("./pages/app/settings/InstellingenDemodataPage"));
const InstellingenDemoRunsPage = lazyPagina(() => import("./pages/app/settings/InstellingenDemoRunsPage"));
const InstellingenIncassoPage = lazyPagina(() => import("./pages/app/settings/InstellingenIncassoPage"));
const InstellingenColettePage = lazyPagina(() => import("./pages/app/settings/InstellingenColettePage"));
const InstellingenAutomatiseringPage = lazyPagina(
  () => import("./pages/app/settings/InstellingenAutomatiseringPage"),
);
const InstellingenFysiekePostPage = lazyPagina(() => import("./pages/app/settings/InstellingenFysiekePostPage"));
const RecoveryDesignerPage = lazyPagina(() => import("./pages/app/settings/RecoveryDesignerPage"));
const RecoveryDesignerDetailPage = lazyPagina(
  () => import("./pages/app/settings/RecoveryDesignerDetailPage"),
);
const InstellingenLegalPartnersPage = lazyPagina(() => import("./pages/app/settings/InstellingenLegalPartnersPage"));
const InstellingenDerdenrekeningPage = lazyPagina(() => import("./pages/app/settings/InstellingenDerdenrekeningPage"));
const InstellingenEmailSignaturePage = lazyPagina(() => import("./pages/app/settings/InstellingenEmailSignaturePage"));
const InstellingenAfzenderidentiteitPage = lazyPagina(
  () => import("./pages/app/settings/InstellingenAfzenderidentiteitPage"),
);
const InstellingenMicrosoft365Page = lazyPagina(
  () => import("./pages/app/settings/InstellingenMicrosoft365Page"),
);
const InstellingenDebiteurportaalContactPage = lazyPagina(
  () => import("./pages/app/settings/InstellingenDebiteurportaalContactPage"),
);
const InstellingenBriefhoofdingPage = lazyPagina(
  () => import("./pages/app/settings/InstellingenBriefhoofdingPage"),
);
const TenantUsersListPage = lazyPagina(() => import("./pages/app/tenant-users/TenantUsersListPage"));
const TenantUserNewPage = lazyPagina(() => import("./pages/app/tenant-users/TenantUserNewPage"));
const TenantUserEditPage = lazyPagina(() => import("./pages/app/tenant-users/TenantUserEditPage"));
const IntakeValidatieMatrixPage = lazyPagina(() => import("./pages/app/IntakeValidatieMatrixPage"));
const WorkflowDocsPage = lazyPagina(() => import("./pages/app/WorkflowDocsPage"));
const IntegrationsPage = lazyPagina(() => import("./pages/app/IntegrationsPage"));
const OpenBankingIntegrationsPage = lazyPagina(() => import("./pages/app/OpenBankingIntegrationsPage"));
const IntegrationMultiSafepayPage = lazyPagina(() => import("./pages/app/integrations/IntegrationMultiSafepayPage"));
const IntegrationOnlinefactDetailPage = lazyPagina(() => import("./pages/app/integrations/IntegrationOnlinefactDetailPage"));
const IntegrationTeamleaderDetailPage = lazyPagina(() => import("./pages/app/integrations/IntegrationTeamleaderDetailPage"));
const IntegrationOdooDetailPage = lazyPagina(() => import("./pages/app/integrations/IntegrationOdooDetailPage"));
const IntegrationCompanywebDetailPage = lazyPagina(() => import("./pages/app/integrations/IntegrationCompanywebDetailPage"));
const IntegrationCreditsafeDetailPage = lazyPagina(() => import("./pages/app/integrations/IntegrationCreditsafeDetailPage"));
const IntegrationEasyPostDetailPage = lazyPagina(() => import("./pages/app/integrations/IntegrationEasyPostDetailPage"));
const BetalingenPage = lazyPagina(() => import("./pages/app/BetalingenPage"));
const BetalingSimulerenPage = lazyPagina(() => import("./pages/app/BetalingSimulerenPage"));
const UitbetalingenPage = lazyPagina(() => import("./pages/app/UitbetalingenPage"));
const FacturatiePage = lazyPagina(() => import("./pages/app/FacturatiePage"));
const AfrekeningenPage = lazyPagina(() => import("./pages/app/AfrekeningenPage"));
const OfficeTimePage = lazyPagina(() => import("./pages/app/OfficeTimePage"));
const PlatformAdminAccessPage = lazyPagina(() => import("./pages/app/platform/PlatformAdminAccessPage"));
const PlatformSupportGrantsPage = lazyPagina(() => import("./pages/app/platform/PlatformSupportGrantsPage"));
const PlatformSupportPage = lazyPagina(() => import("./pages/app/platform/PlatformSupportPage"));
const SupportConsolePage = lazyPagina(() => import("./pages/app/platform/SupportConsolePage"));
const PlatformBillingPage = lazyPagina(() => import("./pages/app/platform/PlatformBillingPage"));
const PlatformPricingPage = lazyPagina(() => import("./pages/app/platform/PlatformPricingPage"));
const PlatformHandelsrentePage = lazyPagina(() => import("./pages/app/platform/PlatformHandelsrentePage"));
const UnmatchedInboxPage = lazyPagina(() => import("./pages/app/UnmatchedInboxPage"));

function RouteFallback() {
  return <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">Laden...</div>;
}

function ProtectedRoute() {
  const { session, loading } = useAuth();
  const userId = session?.user?.id ?? null;
  const authPayload = decodeJwtPayload(session?.access_token);
  const signedOutRef = useRef(false);

  useEffect(() => {
    signedOutRef.current = false;
  }, [userId]);

  const activeQuery = useQuery({
    queryKey: ["auth-app-access", userId, authPayload?.session_id ?? null, authPayload?.aal ?? null],
    queryFn: () => fetchUserAppAccessAllowed(userId!),
    enabled: Boolean(userId) && !loading,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!activeQuery.isSuccess || activeQuery.data !== false || signedOutRef.current) {
      return;
    }
    signedOutRef.current = true;
    void supabase.auth.signOut();
  }, [activeQuery.isSuccess, activeQuery.data]);

  // Een ingetrokken sessie (bv. na een MFA-upgrade) laat de opgeslagen tokens staan,
  // waarna deze query blijft falen en de gebruiker op het foutscherm hieronder strandt
  // zonder uitweg. Alleen bij een expliciet sessie-oordeel opruimen — nooit bij een
  // netwerkfout of 5xx, want dan zou een hapering iedereen uitloggen.
  useEffect(() => {
    if (!activeQuery.isError || signedOutRef.current) {
      return;
    }
    if (!isDeadSessionError(activeQuery.error)) {
      return;
    }
    signedOutRef.current = true;
    // Operationeel relevant: de gebruiker wordt hier uitgelogd zonder dat hij daarom
    // vroeg. Classificeert als `security` en komt dus als WARNING binnen, niet als
    // incident — een ingetrokken sessie is normaal na een MFA-upgrade. Wél willen we
    // zien wanneer het er ineens veel zijn.
    reportHandledError(activeQuery.error, {
      component: "protected-route",
      operation: "dead_session_cleanup",
      userId,
      code: (activeQuery.error as { code?: string } | null)?.code ?? "session_not_found",
    });
    void supabase.auth.signOut({ scope: "local" });
  }, [activeQuery.isError, activeQuery.error]);

  /*
   * DE TOEGANGSCONTROLE DIE NIET LUKTE, ÉÉN KEER MELDEN.
   *
   * Tot 15-09-2026 meldde de loginpagina dit — maar daar werd ook een verzoek
   * geteld dat door een navigatie was AFGEBROKEN, en dat is geen storing. Hier
   * telt alleen wat de gebruiker werkelijk raakt: react-query heeft zijn drie
   * pogingen al gedaan en het foutscherm hieronder staat nu op zijn beeld.
   *
   * Begrensd op ÉÉN melding per foutepisode: zonder die grendel zou elke
   * `refetchOnWindowFocus` bij een wegvallend netwerk opnieuw melden. De
   * grendel gaat pas weer open zodra de controle slaagt.
   *
   * Een dode sessie valt hier buiten: die is hierboven al gemeld, als warning.
   */
  const toegangsfoutGemeldRef = useRef(false);
  useEffect(() => {
    if (activeQuery.isSuccess) {
      toegangsfoutGemeldRef.current = false;
      return;
    }
    if (!activeQuery.isError || toegangsfoutGemeldRef.current) return;
    if (isDeadSessionError(activeQuery.error)) return;
    toegangsfoutGemeldRef.current = true;
    reportHandledError(activeQuery.error, {
      component: "protected-route",
      operation: "app_access_check",
      userId,
      // `code` en `httpStatus` samen maken het verschil zichtbaar tussen een
      // fetch zonder antwoord (`network_unreachable`, status 0) en een server
      // die wél antwoordde. Precies dat onderscheid ontbrak in de oude melding.
      code: (activeQuery.error as { code?: string } | null)?.code ?? null,
      httpStatus: (activeQuery.error as { status?: number } | null)?.status ?? null,
    });
  }, [activeQuery.isSuccess, activeQuery.isError, activeQuery.error, userId]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">Laden...</div>;
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (activeQuery.isLoading) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">Laden...</div>;
  }

  if (activeQuery.isError) {
    // Tot 07-08-2026 was dit een doodlopend scherm: "probeer opnieuw in te loggen"
    // zonder enige manier om dat te dóen. De enige uitweg was een incognitovenster.
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-sm text-red-600">Kon accountstatus niet laden.</p>
        <p className="max-w-md text-sm text-muted-foreground">
          Dit kan een tijdelijke storing zijn. Helpt opnieuw proberen niet, log dan opnieuw in.
        </p>
        <div className="flex gap-2">
          <CollectrButton
            type="button"
            variant="outline"
            onClick={() => void activeQuery.refetch()}
          >
            Opnieuw proberen
          </CollectrButton>
          <CollectrButton
            type="button"

            onClick={() => {
              void supabase.auth.signOut({ scope: "local" });
            }}
          >
            Opnieuw inloggen
          </CollectrButton>
        </div>
      </div>
    );
  }

  if (activeQuery.data === false) {
    return <Navigate to="/login" replace state={{ deactivated: true }} />;
  }

  return <AdminMfaGate />;
}

/**
 * must-19 — beheerders (super_admin/tenant_admin) moeten een tweede factor
 * hebben en die per sessie doorlopen. Bestaande beheerders kregen 30 dagen
 * overgangstijd; daarna fail-closed.
 *
 * Bij een fout in de statuscontrole wordt NIET doorgelaten: een gate die bij
 * twijfel opengaat is geen gate. De gebruiker krijgt een expliciete melding in
 * plaats van stilzwijgend toegang.
 */

function SuperAdminRoute() {
  const { session, user, loading: authLoading } = useAuth();
  const actorId = session?.user?.id ?? user?.id;
  const { role, isLoading: roleLoading, isError } = useCurrentUserRole(actorId);
  if (authLoading || roleLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">Laden...</div>
    );
  }
  if (isError) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-sm text-red-600">
        Kon gebruikersrol niet laden. Controleer of dit account een rij in public.users heeft.
      </div>
    );
  }
  if (!canAccessPlatformAdmin(role)) {
    return <Navigate to="/app/explorer" replace />;
  }
  return <Outlet />;
}

/**
 * g4-c7 — de supportwerkplek. Bewust een eigen poort naast SuperAdminRoute:
 * support is geen platformbeheer en mag daar niets van zien. De poort bepaalt
 * alleen wie het scherm opent; WAT er te zien is bepaalt de lopende grant in de
 * database, niet deze component.
 */
function PlatformSupportRoute() {
  const { session, user, loading: authLoading } = useAuth();
  const actorId = session?.user?.id ?? user?.id;
  const { role, isLoading: roleLoading, isError } = useCurrentUserRole(actorId);
  if (authLoading || roleLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">Laden...</div>
    );
  }
  if (isError) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-sm text-red-600">
        Kon gebruikersrol niet laden. Controleer of dit account een rij in public.users heeft.
      </div>
    );
  }
  if (!isPlatformSupportRole(role)) {
    return <Navigate to="/app" replace />;
  }
  return <Outlet />;
}

function LoginRoute() {
  const { session, loading } = useAuth();
  if (loading) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">Laden...</div>;
  }
  /** Use /app (ProtectedRoute) so `is_active` is enforced before AppHomeRedirect. */
  if (session) {
    return <Navigate to="/app" replace />;
  }
  return (
    <Suspense fallback={<div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">Laden...</div>}>
      <LoginPage />
    </Suspense>
  );
}

/*
 * Een pagina die al openstond tijdens een deploy verwijst naar chunknamen die
 * na de nieuwe build niet meer bestaan. Vite meldt dat als `vite:preloadError`,
 * en het juiste antwoord is herladen — dan haalt de browser de verse index.html
 * op. De teller begrenst dat tot hoogstens twee pogingen; daarna valt de
 * gebruiker door naar RouteErrorPage, die het uitlegt in plaats van eindeloos
 * te verversen.
 *
 * ⚠️ WAT HIER MIS WAS (05-08-2026, gemeten op productie). De teller was een vlag
 * die bij het `load`-event weer werd gewist, "want dan is het gelukt". Maar
 * `load` vuurt bij ELKE documentlading — óók bij de herlading die deze handler
 * zelf net veroorzaakte. De vlag overleefde dus nooit een reload en kon het
 * aantal herladingen daarom niet begrenzen: de pagina bleef verversen. De
 * teller wordt nu uitsluitend gewist door `lazyPagina`, wanneer er werkelijk
 * een chunk is binnengekomen.
 */
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (_event) => {
    noteerPreloadFout(window.sessionStorage);
    // Budget op: niet nog eens herladen, maar de fout laten doorlopen.
    if (!magNogHerladen(window.sessionStorage)) return;
    // Laat de oorspronkelijke importfout afwijzen. preventDefault laat Vite
    // undefined opleveren: React.lazy faalt dan op .default terwijl de
    // herlading nog onderweg is. De routeboundary biedt het herstelpad.
    window.location.reload();
  });
}

const router = createBrowserRouter(
  createRoutesFromElements(
    /*
      Pathloze wortelroute met errorElement: de data router vangt fouten uit een
      route zelf af en komt daardoor NOOIT bij de React-boundaries eromheen.
      Zonder dit ziet een klant de ontwikkelaarsmelding van React Router.
    */
    <Route errorElement={<RouteErrorPage />}>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/uitnodiging" element={<Suspense fallback={<RouteFallback />}><UitnodigingPage /></Suspense>} />
            <Route path="/legal-aanvraag" element={<Suspense fallback={<RouteFallback />}><LegalRequestPage /></Suspense>} />
            {/*
              must-42: buiten LoginRoute gehouden — die stuurt bij een bestaande
              sessie door naar /app, en de recovery-link levert nu juist een
              (beperkte) sessie op. De pagina moet dus bereikbaar blijven.
            */}
            {/* must-19: bereikbaar met een aal1-sessie — dat is juist de toestand waarin een beheerder hier belandt. */}
            <Route
              path="/2fa"
              element={
                <Suspense
                  fallback={<div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">Laden...</div>}
                >
                  <TweefactorPage />
                </Suspense>
              }
            />
            {/*
              Het pad heette eerst /tweefactor en is naar /2fa gebracht. De oude
              route blijft als doorverwijzing bestaan: hij is al aan beheerders
              gecommuniceerd en een beheerder die hier strandt is per definitie
              iemand die NIET meer binnenkomt — dat is het slechtste moment voor
              een doodlopende link.
            */}
            <Route path="/tweefactor" element={<Navigate to="/2fa" replace />} />
            {/*
              WP-UI0.1 — het dossierbedieningsprototype. Buiten `ProtectedRoute`:
              het leest uitsluitend fictieve fixtures, dus er is geen tenant,
              dossier of persoonsgegeven om af te schermen, en binnen `/app` zou
              het de sessie- en rolqueries van de app-shell meekrijgen. De gate
              staat bij `dossierbedieningPrototypeEnabled` hierboven; geen
              menu-ingang, want dit is onderzoeksmateriaal.
            */}
            {dossierbedieningPrototypeEnabled && DossierbedieningPrototypePage != null ? (
              <Route
                path="/prototypes/dossierbediening"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <DossierbedieningPrototypePage />
                  </Suspense>
                }
              />
            ) : null}
            <Route
              path="/wachtwoord-herstellen"
              element={
                <Suspense
                  fallback={<div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">Laden...</div>}
                >
                  <WachtwoordHerstellenPage />
                </Suspense>
              }
            />
            <Route
              path="/legal-export/:token"
              element={
                <Suspense
                  fallback={<div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">Laden...</div>}
                >
                  <LegalExportPage />
                </Suspense>
              }
            />
            <Route element={<ProtectedRoute />}>
              <Route
                path="/app"
                element={
                  <Suspense fallback={<div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">Laden...</div>}>
                    <AppLayout />
                  </Suspense>
                }
              >
                <Route index element={<AppHomeRedirect />} />
                <Route
                  path="explorer"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <ExplorerPage />
                    </Suspense>
                  }
                />
                <Route
                  path="dashboard"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <DashboardPage />
                    </Suspense>
                  }
                />
                {/*
                  g5-c4: bewust geen routegate. Elke gebruiker van de tenant mag
                  weten wat er aan zijn omgeving ontbreekt; het scherm toont
                  uitsluitend tellingen en toestanden, geen dossierinhoud, en de
                  oplosknoppen verschijnen alleen bij de rollen die de handeling
                  werkelijk mogen doen.
                */}
                <Route
                  path="activatiecentrum"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <ActivatiecentrumPage />
                    </Suspense>
                  }
                />
                <Route path="startklaar" element={<Navigate to="/app/activatiecentrum" replace />} />
                {/*
                  De onboardingpagina hoort BINNEN de gate te staan: zou ze
                  erbuiten vallen, dan blokkeert de gate de enige route waarmee
                  de gebruiker hem kan opheffen. `moetNaarOnboarding` zondert dit
                  pad daarom expliciet uit.
                */}
                <Route
                  path="onboarding"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <OnboardingPage />
                    </Suspense>
                  }
                />
                <Route
                  path="dossiers"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <DossiersPage />
                    </Suspense>
                  }
                />
                <Route
                  path="archief"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <ArchiefPage />
                    </Suspense>
                  }
                />
                <Route
                  path="dossiers/pdf"
                  element={<Suspense fallback={<RouteFallback />}><PdfIntakePage /></Suspense>}
                />
                <Route
                  path="dossiers/intake"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <DossierIntakePage />
                    </Suspense>
                  }
                />
                <Route
                  path="pre-dossier-intakes"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <PreDossierIntakesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="pre-dossier-intakes/:id"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <PreDossierIntakeDetailPage />
                    </Suspense>
                  }
                />
                <Route
                  path="inbox"
                  element={
                    <Navigate to="/app/intake-queue?source=inbox&tab=on_hold" replace />
                  }
                />
                <Route
                  path="intake-queue"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <IntakeQueuePage />
                    </Suspense>
                  }
                />
                {csvImportEnabled ? (
                  <Route
                    path="intake-import"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <ImportWizardPage />
                      </Suspense>
                    }
                  />
                ) : null}
                {/*
                  Het tweede bevestigingsmoment: promotie maakte dossiers, hier
                  worden ze gecontroleerd, geselecteerd en ingepland. Bewust een
                  eigen route: "Startklaar" bestond al en gaat over de inrichting
                  van de omgeving, niet over deze dossiers.
                */}
                <Route
                  path="dossier-activatie"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <DossierActivatiePage />
                    </Suspense>
                  }
                />
                <Route
                  path="intake-queue/:intakeItemId"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <IntakeQueueDetailPage />
                    </Suspense>
                  }
                />
                {/*
                  UI-1 — HET CANONIEKE DOSSIERPAD (founderbesluit 07-09-2026,
                  punt A).

                  `/app/dossiers/:id` opent sinds deze slice de V2-weergave. De
                  vlag `VITE_DOSSIER_V2_ENABLED` is daarmee vervallen: een vlag
                  die maar één stand meer kent is geen vlag, en een tweede stand
                  zou betekenen dat twee collega's een ander scherm zien op
                  hetzelfde dossier — precies wat de nulmeting vaststelde.

                  De klassieke pagina blijft TIJDELIJK bereikbaar op `/legacy`,
                  uitsluitend via het overflowmenu. Er wordt daar niets nieuws
                  meer op gebouwd.
                */}
                <Route
                  path="uren"
                  element={<Suspense fallback={<RouteFallback />}><OfficeTimePage /></Suspense>}
                />
                <Route
                  path="uren/kantoor"
                  element={<Suspense fallback={<RouteFallback />}><OfficeTimePage office /></Suspense>}
                />
                <Route
                  path="dossiers/:id"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <DossierDetailPage variant="v2" />
                    </Suspense>
                  }
                />
                <Route
                  path="dossiers/:id/legacy"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <DossierDetailPage variant="v1" />
                    </Suspense>
                  }
                />
                {/*
                  Het oude experimentpad blijft bestaan als DOORVERWIJZING en
                  niet als tweede scherm: bestaande bladwijzers en gedeelde
                  links mogen niet op een 404 landen, maar ze mogen evenmin een
                  eigen weergave in leven houden.
                */}
                <Route path="dossiers/:id/v2" element={<DossierV2NaarCanoniekPad />} />
                <Route path="legal-executor/:dossierId" element={<LegalExecutorToDossierRedirect />} />
                <Route
                  path="intake-flow-control"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <IntakeFlowControlPage />
                    </Suspense>
                  }
                />
                {/*
                  g4-c8: de rolgrens staat in de RLS-policy en in de edge, niet
                  hier — dit scherm toont hem alleen. Vandaar geen routegate:
                  die zou een browser tegenhouden en verder niets (must-19).
                */}
                <Route
                  path="bewaarplichten"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <LegalHoldPage />
                    </Suspense>
                  }
                />
                <Route
                  path="workqueue"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <WorkqueuePage />
                    </Suspense>
                  }
                />
                <Route
                  path="follow-up"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <FollowUpPage />
                    </Suspense>
                  }
                />
                <Route
                  path="overdrachten"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <OverdrachtenPage />
                    </Suspense>
                  }
                />
                <Route
                  path="opvolging"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <OpvolgingPage />
                    </Suspense>
                  }
                />
                <Route
                  path="legal-cases"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <LegalCasesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="print-queue"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <PrintQueuePage />
                    </Suspense>
                  }
                />
                <Route
                  path="exceptions"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <ExceptionsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="workflow-monitor"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <WorkflowMonitorPage />
                    </Suspense>
                  }
                />
                <Route
                  path="inbox/unmatched"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <UnmatchedInboxPage />
                    </Suspense>
                  }
                />
                <Route
                  path="templates"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <TemplatesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="templates/:id"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <TemplateDetailPage />
                    </Suspense>
                  }
                />
                <Route
                  path="templates/:id/edit"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <TemplateEditPage />
                    </Suspense>
                  }
                />
                {/*
                  De oude pagina "Workflow configuratie" is verwijderd: zij schreef als
                  enige clientpad rechtstreeks in de live workflowconfiguratie (states,
                  transition_rules, workflow_state_actions, workflow_publications) buiten
                  de Recovery Designer om. De ROUTE blijft bestaan als doorverwijzing,
                  want operators hebben dit pad in hun bladwijzers staan.
                */}
                <Route path="workflow-config" element={<RecoveryDesignerListRedirect />} />
                <Route
                  path="recovery-designer"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <RecoveryDesignerPage />
                    </Suspense>
                  }
                />
                <Route
                  path="recovery-designer/:programId"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <RecoveryDesignerDetailPage />
                    </Suspense>
                  }
                />
                <Route
                  path="debiteuren"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <DebiteurenPage />
                    </Suspense>
                  }
                />
                <Route
                  path="debiteuren/:debiteurId"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <DebiteurDetailPage />
                    </Suspense>
                  }
                />
                <Route
                  path="opdrachtgevers"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <OpdrachtgeversListPage />
                    </Suspense>
                  }
                />
                <Route
                  path="opdrachtgevers/nieuw"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <OpdrachtgeverWizardPage />
                    </Suspense>
                  }
                />
                <Route
                  path="opdrachtgevers/:id/bewerken"
                  element={<OpdrachtgeverDetailRedirect />}
                />
                <Route
                  path="opdrachtgevers/:id/projecten/nieuw"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <CreditorProjectNewPage />
                    </Suspense>
                  }
                />
                <Route
                  path="opdrachtgevers/:creditorId/projecten/:projectId"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <CreditorProjectDetailPage />
                    </Suspense>
                  }
                />
                <Route
                  path="opdrachtgevers/:id"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <OpdrachtgeverDetailPage />
                    </Suspense>
                  }
                />
                <Route
                  path="instellingen"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <SettingsHomePage />
                    </Suspense>
                  }
                />
                <Route element={<PaymentReconciliationRoute />}>
                  <Route
                    path="finance/betaling-simuleren"
                    element={<BetalingSimulerenPage />}
                  />
                  <Route
                    path="betalingen"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <BetalingenPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="uitbetalingen"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <UitbetalingenPage />
                      </Suspense>
                    }
                  />
                </Route>
                {BILLING_UI_ENABLED ? (
                  <>
                    {/* ADR-055: canonieke route /app/verbruik; /app/facturatie blijft als redirect werken. */}
                    <Route
                      path="verbruik"
                      element={
                        <Suspense fallback={<RouteFallback />}>
                          <FacturatiePage />
                        </Suspense>
                      }
                    />
                    <Route path="facturatie" element={<Navigate to="/app/verbruik" replace />} />
                  </>
                ) : null}
                {SETTLEMENT_UI_ENABLED ? (
                  <Route element={<PaymentReconciliationRoute />}>
                    <Route
                      path="afrekeningen"
                      element={
                        <Suspense fallback={<RouteFallback />}>
                          <AfrekeningenPage />
                        </Suspense>
                      }
                    />
                  </Route>
                ) : null}
                <Route element={<OpenBankingIntegrationRoute />}>
                  <Route
                    path="integraties"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <IntegrationsPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="integraties/open-banking"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <OpenBankingIntegrationsPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="integraties/onlinefact"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <IntegrationOnlinefactDetailPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="integraties/odoo"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <IntegrationOdooDetailPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="integraties/teamleader"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <IntegrationTeamleaderDetailPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="integraties/companyweb"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <IntegrationCompanywebDetailPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="integraties/creditsafe"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <IntegrationCreditsafeDetailPage />
                      </Suspense>
                    }
                  />
                </Route>
                <Route element={<PaymentIntegrationRoute />}>
                  <Route
                    path="integraties/multisafepay"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <IntegrationMultiSafepayPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="instellingen/multisafepay"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <IntegrationMultiSafepayPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="integraties/easypost"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <IntegrationEasyPostDetailPage />
                      </Suspense>
                    }
                  />
                </Route>
                <Route element={<TenantGovernanceRoute />}>
                  <Route
                    path="instellingen/gebruikers"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <TenantUsersListPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="instellingen/gebruikers/nieuw"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <TenantUserNewPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="instellingen/gebruikers/:id/bewerken"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <TenantUserEditPage />
                      </Suspense>
                    }
                  />
                </Route>
                <Route
                  path="instellingen/intake-validatie-matrix"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <IntakeValidatieMatrixPage />
                    </Suspense>
                  }
                />
                <Route
                  path="instellingen/workflow"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <WorkflowDocsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="instellingen/dossiernummering"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InstellingenDossiernummeringPage />
                    </Suspense>
                  }
                />
                <Route
                  path="instellingen/standaard-tarieven"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InstellingenStandaardTarievenPage />
                    </Suspense>
                  }
                />
                <Route
                  path="instellingen/kosten"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InstellingenKostenPage />
                    </Suspense>
                  }
                />
                <Route path="instellingen/juridische-documenten" element={<Suspense fallback={<RouteFallback />}><InstellingenJuridischeDocumentenPage /></Suspense>} />
                <Route
                  path="instellingen/kosten-interest"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InstellingenKostenInterestPage />
                    </Suspense>
                  }
                />
                <Route
                  path="instellingen/demodata"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InstellingenDemodataPage />
                    </Suspense>
                  }
                />
                <Route
                  path="instellingen/demo-runs"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InstellingenDemoRunsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="instellingen/automatisering"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InstellingenAutomatiseringPage />
                    </Suspense>
                  }
                />
                <Route
                  path="instellingen/incasso"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InstellingenIncassoPage />
                    </Suspense>
                  }
                />
                <Route
                  path="instellingen/colette"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InstellingenColettePage />
                    </Suspense>
                  }
                />
                <Route
                  path="instellingen/fysieke-post"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InstellingenFysiekePostPage />
                    </Suspense>
                  }
                />
                <Route path="instellingen/incasso-setup" element={<Navigate to="/app/activatiecentrum" replace />} />
                <Route
                  path="instellingen/recovery-designer"
                  element={<RecoveryDesignerListRedirect />}
                />
                <Route
                  path="instellingen/recovery-designer/:programId"
                  element={<RecoveryDesignerDetailRedirect />}
                />
                <Route
                  path="instellingen/recovery-strategieen"
                  element={<RecoveryDesignerListRedirect />}
                />
                <Route
                  path="instellingen/recovery-strategieen/:programId"
                  element={<RecoveryDesignerDetailRedirect />}
                />
                <Route
                  path="instellingen/legal-partners"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InstellingenLegalPartnersPage />
                    </Suspense>
                  }
                />
                <Route
                  path="instellingen/derdenrekening"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InstellingenDerdenrekeningPage />
                    </Suspense>
                  }
                />
                <Route
                  path="instellingen/afzenderidentiteit"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InstellingenAfzenderidentiteitPage />
                    </Suspense>
                  }
                />
                <Route
                  path="instellingen/microsoft-365"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InstellingenMicrosoft365Page />
                    </Suspense>
                  }
                />
                <Route
                  path="instellingen/email-handtekening"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InstellingenEmailSignaturePage />
                    </Suspense>
                  }
                />
                <Route
                  path="instellingen/debiteurportaal-contact"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InstellingenDebiteurportaalContactPage />
                    </Suspense>
                  }
                />
                <Route
                  path="instellingen/briefhoofding"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <InstellingenBriefhoofdingPage />
                    </Suspense>
                  }
                />
                <Route path="settings/kosten" element={<Navigate to="/app/instellingen/kosten" replace />} />
                <Route element={<SuperAdminRoute />}>
                  <Route path="platform/legal-docs/:id?" element={<Suspense fallback={<RouteFallback />}><PlatformLegalDocsPage /></Suspense>} />
                  <Route
                    path="platform/support/:section"
                    element={<Suspense fallback={<RouteFallback />}><PlatformSupportPage /></Suspense>}
                  />
                  <Route
                    path="internal/platform"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <InternalPlatformAdminPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="platform/tenants"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <PlatformTenantsListPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="platform/tenants/nieuw"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <Navigate to="/app/platform/legal-docs" replace />
                      </Suspense>
                    }
                  />
                  <Route
                    path="platform/tenants/:id/provision-admin"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <PlatformProvisionAdminPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="platform/tenants/:id"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <PlatformTenantDetailPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="platform/recovery-library"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <PlatformRecoveryLibraryPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="platform/services"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <PlatformServicesPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="platform/colette-config"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <PlatformColetteConfigPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="platform/billing"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <PlatformBillingPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="platform/pricing"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <PlatformPricingPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="platform/wettelijke-handelsrente"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <PlatformHandelsrentePage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="platform/beheerderstoegang"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <PlatformAdminAccessPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="platform/support-grants"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <PlatformSupportGrantsPage />
                      </Suspense>
                    }
                  />
                </Route>
                <Route element={<PlatformSupportRoute />}>
                  <Route
                    path="support"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <SupportConsolePage />
                      </Suspense>
                    }
                  />
                </Route>
                {/* Unmatched /app/* → /app index (AppHomeRedirect picks platform vs explorer). */}
                <Route path="*" element={<Navigate to="/app" replace />} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/login" replace />} />
    </Route>,
  ),
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Sentry.ErrorBoundary fallback={<p>Er is iets misgegaan. Herlaad de pagina.</p>}>
        <RouterProvider router={router} />
      </Sentry.ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
